import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  CheckSquare,
  GraduationCap,
  Home,
  IndianRupee,
  KanbanSquare,
  LogOut,
  Menu,
  Search,
  Settings,
  Users,
} from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { useAuthStore } from "@/store/auth";
import { Avatar, Toaster } from "@/components/ui";
import { ROLE_LABEL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/api/lookups";
import type { StudentBrief, User } from "@/types";
import { StageBadge } from "@/components/shared";

function NavItem({ to, icon: Icon, label, end }: { to: string; icon: typeof Home; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition",
          isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white",
        )
      }
    >
      <Icon className="size-[18px]" />
      {label}
    </NavLink>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["search", q],
    enabled: q.trim().length >= 2,
    queryFn: async () =>
      (
        await apiClient.get<{
          students: StudentBrief[];
          leads: { id: number; student_name: string; vertical: string; stage: string }[];
        }>("/search/", { params: { q } })
      ).data,
  });

  const go = (path: string) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  return (
    <div className="relative w-full max-w-sm">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search students, leads, phone…"
        className="w-full rounded-xl border border-line bg-canvas py-2 pl-9 pr-3 text-sm outline-none focus:border-brand focus:bg-white"
      />
      {open && q.trim().length >= 2 && data && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-96 overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-xl">
          {data.students.length === 0 && data.leads.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted">No matches</div>
          )}
          {data.students.map((s) => (
            <button
              key={`s${s.id}`}
              onMouseDown={() => go(`/students/${s.id}`)}
              className="flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-canvas"
            >
              <span>
                <span className="block text-sm font-semibold">{s.name}</span>
                <span className="text-xs text-muted">{s.phone || s.email}</span>
              </span>
              <span className="text-[10px] font-bold uppercase text-muted">Student</span>
            </button>
          ))}
          {data.leads.map((l) => (
            <button
              key={`l${l.id}`}
              onMouseDown={() => go(`/leads/${l.id}`)}
              className="flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-canvas"
            >
              <span className="text-sm font-semibold">{l.student_name}</span>
              <StageBadge stage={l.stage} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppLayout() {
  const user = useCurrentUser();
  const logout = useAuthStore((s) => s.logout);
  const setUser = useAuthStore((s) => s.setUser);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Refresh the profile on load (role/verticals may have changed).
  useEffect(() => {
    apiClient
      .get<User>("/users/me/")
      .then((r) => setUser(r.data))
      .catch(() => undefined);
  }, [setUser]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  if (!user) return null;

  const sidebar = (
    <aside className="flex h-full w-64 flex-col bg-navy p-4 text-white">
      <Link to="/" className="mb-6 flex items-center gap-3 px-2 pt-1">
        <div className="grid size-10 place-items-center rounded-xl bg-white/15 font-display font-extrabold">CK</div>
        <div>
          <div className="font-display text-base font-extrabold leading-none">CampusKey</div>
          <div className="mt-1 text-[9px] font-bold tracking-[0.2em] text-white/60">EDUCATION CRM</div>
        </div>
      </Link>
      <nav className="flex-1 space-y-1 overflow-y-auto">
        <div className="px-3 pb-1 pt-2 text-[10px] font-bold tracking-widest text-white/40">WORKSPACE</div>
        <NavItem to="/" icon={Home} label="My Day" end />
        <NavItem to="/pipeline" icon={KanbanSquare} label="Lead pipeline" />
        <NavItem to="/students" icon={Users} label="Students" />
        <NavItem to="/enrolments" icon={GraduationCap} label="Enrolments" />
        <NavItem to="/tasks" icon={CheckSquare} label="Tasks" />
        {user.can_access_finance && (
          <>
            <div className="px-3 pb-1 pt-4 text-[10px] font-bold tracking-widest text-white/40">FINANCE</div>
            <NavItem to="/payments" icon={IndianRupee} label="Payments" />
            <NavItem to="/reports" icon={BarChart3} label="Reports" />
          </>
        )}
        {user.can_manage && (
          <>
            <div className="px-3 pb-1 pt-4 text-[10px] font-bold tracking-widest text-white/40">MANAGE</div>
            <NavItem to="/admin" icon={Settings} label="Admin" />
          </>
        )}
      </nav>
      <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/10 p-3">
        <Avatar name={user.name} className="bg-white/20 text-white" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{user.name}</div>
          <div className="truncate text-[11px] text-white/60">{ROLE_LABEL[user.role]}</div>
        </div>
        <button
          onClick={logout}
          title="Sign out"
          className="cursor-pointer rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-full">
      <div className="hidden lg:block">{sidebar}</div>
      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-navy/50" />
          <div className="relative h-full w-64" onClick={(e) => e.stopPropagation()}>
            {sidebar}
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-line bg-white px-4 py-3 lg:px-8">
          <button
            className="cursor-pointer rounded-lg p-1.5 text-ink hover:bg-canvas lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Menu"
          >
            <Menu className="size-5" />
          </button>
          <GlobalSearch />
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster />
    </div>
  );
}
